import { useCallback, type ReactNode } from 'react'
import InputBase, { type InputBaseComponentProps } from '@mui/material/InputBase'
import Search from '@mui/icons-material/Search'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'
import { Kbd } from './Kbd'

const styles = {
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        padding: (theme: Theme) => theme.spacing(1, 2),
        borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    },
    icon: {
        display: 'flex',
        marginRight: (theme: Theme) => theme.spacing(1),
        opacity: 0.54,
    },
    input: {
        flex: 1,
        fontSize: '1rem',
    },
    hint: {
        opacity: 0.4,
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
        marginLeft: (theme: Theme) => theme.spacing(1),
    },
}

interface PaletteInputProps {
    value: string
    onChange: (value: string) => void
    onKeyDown: (e: React.KeyboardEvent) => void
    placeholder?: string
    /** Accessible name, for inputs whose placeholder alone does not describe them */
    label?: string
    icon?: ReactNode
    /** Keyboard hint at the end of the row */
    hint?: ReactNode
    /** Extra attributes for the input element, such as combobox ARIA state */
    inputProps?: InputBaseComponentProps
}

/** The focused input row at the top of a search or command palette dialog. */
export const PaletteInput = ({
    value,
    onChange,
    onKeyDown,
    placeholder = 'Search...',
    label,
    icon = <Search />,
    hint = (
        <>
            <Kbd>Esc</Kbd> to close
        </>
    ),
    inputProps,
}: PaletteInputProps) => {
    const inputRef = useCallback((el: HTMLInputElement | null) => {
        if (el) setTimeout(() => el.focus(), 0)
    }, [])

    return (
        <Box sx={styles.wrapper}>
            <Box sx={styles.icon}>{icon}</Box>
            <InputBase
                sx={styles.input}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                inputRef={inputRef}
                inputProps={{ ...inputProps, 'aria-label': label }}
                fullWidth
            />
            <Typography sx={styles.hint}>{hint}</Typography>
        </Box>
    )
}
